import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changePassword, getAdminData, popUp, updateAdmin } from '../Redux/Auth/AuthSlice';
import { Notify } from '../CommonPopup/Popup';
import judicialLogo from '../Images/judicialLogo.png';
import '../Style/Profile.css';
import Spinner from '../Spinner/Spinner';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

// Define the validation schema for change password
const changePasswordSchema = yup.object().shape({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),
  confirmNewPassword: yup
    .string()
    .required('Confirm new password is required')
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match'),
});

// Define the validation schema for profile update
const profileSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
});

const EditProfile = () => {
  const dispatch = useDispatch();
  const selector = useSelector((s) => s.AuthSlice);

  const loader = selector.loader;
  const getAdminAllData = selector.getAdminAllData || {};

  const [name, setName] = useState(getAdminAllData.name || '');
  const [phone, setPhone] = useState(getAdminAllData.phone || '');
  const [edit, setEdit] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(changePasswordSchema),
  });

  const {
    control: profileControl,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    setValue,
  } = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: getAdminAllData.name || '',
      phone: getAdminAllData.phone || '',
    },
  });

  const updateAdminProfile = (data) => {
    const adminData = {
      name: data.name,
      phone: data.phone,
    };

    dispatch(updateAdmin(adminData))
      .unwrap()
      .then((response) => {
        if (response?.success) {
          Notify({ data: 'success', msg: 'Account has been updated' });
          setEdit(false);
          setName(response.data?.name || data.name);
          setPhone(response.data?.phone || data.phone);
          setValue('name', response.data?.name || data.name);
          setValue('phone', response.data?.phone || data.phone);
          dispatch(getAdminData());
        } else {
          Notify({ data: 'error', msg: response?.message || 'Failed to update profile' });
        }
      })
      .catch((error) => {
        console.error('Update error:', error);
        Notify({ data: 'error', msg: error.message || 'Something went wrong!' });
      });
  };

  const handleEdit = () => {
    setEdit(true);
    setValue('name', name);
    setValue('phone', phone);
  };

  const handleChangePassword = (data) => {
    dispatch(changePassword({ currentPassword: data.currentPassword, newPassword: data.newPassword }))
      .unwrap()
      .then((response) => {
        if (response.success) {
          Notify({ data: 'success', msg: 'New password saved successfully' });
          setShowChangePassword(false);
          reset();
        } else {
          Notify({ data: 'error', msg: response?.message || 'Failed to update password' });
        }
        setTimeout(() => {
          dispatch(popUp(false));
        }, 5000);
      })
      .catch((error) => {
        console.error('Change password error:', error);
        Notify({ data: 'error', msg: error.message || 'Something went wrong!' });
      });
  };

  const handleBack = () => {
    setShowChangePassword(false);
    reset();
  };

  useEffect(() => {
    if (getAdminAllData) {
      setName(getAdminAllData.name || '');
      setPhone(getAdminAllData.phone || '');
      setValue('name', getAdminAllData.name || '');
      setValue('phone', getAdminAllData.phone || '');
    }
  }, [getAdminAllData, setValue]);

  useEffect(() => {
    dispatch(getAdminData());
  }, [dispatch]);

  return (
    <div className="edit-profile-main">
      <div className="logo-container">
        <img className="judicial-logo" src={judicialLogo} alt="Judicial Logo" />
      </div>

      <div className="edit-profile-container">
        {showChangePassword ? (
          <form className="profile-form" onSubmit={handleSubmit(handleChangePassword)}>
            <h2>Change Password</h2>

            {loader ? (
              <div className="spinner-wrapper">
                <Spinner />
              </div>
            ) : (
              <>
                <div className="input-group password-input">
                  <Controller
                    name="currentPassword"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        placeholder="Current Password"
                        label=""
                        type={showCurrent ? 'text' : 'password'}
                        fullWidth
                        variant="outlined"
                        error={!!errors.currentPassword}
                        helperText={errors.currentPassword?.message}
                        {...field}
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            background: '#ffffff',
                            '& fieldset': { borderColor: '#e5e7eb' },
                            '&:hover fieldset': { borderColor: '#d1d5db' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)' },
                          },
                          '& .MuiInputBase-input': {
                            color: '#1f2937',
                          },
                        }}
                      />
                    )}
                  />
                  <span className="toggle-password" onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <FiEyeOff /> : <FiEye />}
                  </span>
                </div>

                <div className="input-group password-input">
                  <Controller
                    name="newPassword"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        placeholder="New Password"
                        label=""
                        type={showNew ? 'text' : 'password'}
                        fullWidth
                        variant="outlined"
                        error={!!errors.newPassword}
                        helperText={errors.newPassword?.message}
                        {...field}
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            background: '#ffffff',
                            '& fieldset': { borderColor: '#e5e7eb' },
                            '&:hover fieldset': { borderColor: '#d1d5db' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)' },
                          },
                          '& .MuiInputBase-input': {
                            color: '#1f2937',
                          },
                        }}
                      />
                    )}
                  />
                  <span className="toggle-password" onClick={() => setShowNew(!showNew)}>
                    {showNew ? <FiEyeOff /> : <FiEye />}
                  </span>
                </div>

                <div className="input-group password-input">
                  <Controller
                    name="confirmNewPassword"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        placeholder="Confirm New Password"
                        label=""
                        type={showConfirm ? 'text' : 'password'}
                        fullWidth
                        variant="outlined"
                        error={!!errors.confirmNewPassword}
                        helperText={errors.confirmNewPassword?.message}
                        {...field}
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            background: '#ffffff',
                            '& fieldset': { borderColor: '#e5e7eb' },
                            '&:hover fieldset': { borderColor: '#d1d5db' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)' },
                          },
                          '& .MuiInputBase-input': {
                            color: '#1f2937',
                          },
                        }}
                      />
                    )}
                  />
                  <span className="toggle-password" onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? <FiEyeOff /> : <FiEye />}
                  </span>
                </div>

                <div className="button-group">
                  <Button
                    type="button"
                    variant="contained"
                    className="reset-button"
                    onClick={handleBack}
                    sx={{
                      background: '#6b7280',
                      borderRadius: '12px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        background: '#4b5563',
                      },
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    className="edit-button"
                    sx={{
                      background: '#3b82f6',
                      borderRadius: '12px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        background: '#2563eb',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                      },
                    }}
                  >
                    Update Password
                  </Button>
                </div>
              </>
            )}
          </form>
        ) : (
          <form className="profile-form" onSubmit={handleProfileSubmit(updateAdminProfile)}>
            <h2>Edit Profile</h2>

            {loader ? (
              <div className="spinner-wrapper">
                <Spinner />
              </div>
            ) : (
              <>
                <div className="input-group">
                  <label className="fw-bold">Full Name</label>
                  <Controller
                    name="name"
                    control={profileControl}
                    render={({ field }) => (
                      <input
                        type="text"
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          setName(e.target.value);
                        }}
                        disabled={!edit}
                        className={profileErrors.name ? 'input-error' : ''}
                        placeholder="Enter your name"
                      />
                    )}
                  />
                  {profileErrors.name && (
                    <span className="error-text">{profileErrors.name.message}</span>
                  )}
                </div>

                <div className="input-group">
                  <label className="fw-bold">Phone Number</label>
                  <Controller
                    name="phone"
                    control={profileControl}
                    render={({ field }) => (
                      <input
                        type="tel"
                        value={field.value}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          field.onChange(value);
                          setPhone(value);
                        }}
                        disabled={!edit}
                        className={profileErrors.phone ? 'input-error' : ''}
                        placeholder="Enter 10-digit phone number"
                        maxLength={10}
                      />
                    )}
                  />
                  {profileErrors.phone && (
                    <span className="error-text">{profileErrors.phone.message}</span>
                  )}
                </div>

                <div className="input-group">
                  <label className="fw-bold">Email</label>
                  <input
                    type="email"
                    value={getAdminAllData.email || ''}
                    disabled
                  />
                </div>

                <div className="button-group">
                  {edit ? (
                    <>
                      <Button
                        type="submit"
                        variant="contained"
                        className="edit-button"
                        sx={{
                          background: '#3b82f6',
                          borderRadius: '12px',
                          padding: '12px 24px',
                          fontSize: '16px',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            background: '#2563eb',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                          },
                        }}
                      >
                        Update Profile
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      className="edit-button"
                      onClick={handleEdit}
                      sx={{
                        background: '#3b82f6',
                        borderRadius: '12px',
                        padding: '12px 24px',
                        fontSize: '16px',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          background: '#2563eb',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        },
                      }}
                    >
                      Edit Profile
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="contained"
                    className="reset-button"
                    onClick={() => setShowChangePassword(true)}
                    sx={{
                      background: '#6b7280',
                      borderRadius: '12px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        background: '#4b5563',
                      },
                    }}
                  >
                    Change Password
                  </Button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default EditProfile;


// import React, { useEffect, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { changePassword, getAdminData, popUp, updateAdmin } from '../Redux/Auth/AuthSlice';
// import { Notify } from '../CommonPopup/Popup';
// import judicialLogo from '../Images/judicialLogo.png';
// import '../Style/Profile.css';
// import Spinner from '../Spinner/Spinner';
// import { FiEye, FiEyeOff } from 'react-icons/fi';
// import { useForm, Controller } from 'react-hook-form';
// import { yupResolver } from '@hookform/resolvers/yup';
// import * as yup from 'yup';
// import TextField from '@mui/material/TextField';
// import Button from '@mui/material/Button';

// // Define the validation schema for change password
// const changePasswordSchema = yup.object().shape({
//   currentPassword: yup.string().required('Current password is required'),
//   newPassword: yup
//     .string()
//     .required('New password is required')
//     .min(8, 'Password must be at least 8 characters')
//     .matches(
//       /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
//       'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
//     ),
//   confirmNewPassword: yup
//     .string()
//     .required('Confirm new password is required')
//     .oneOf([yup.ref('newPassword'), null], 'Passwords must match'),
// });

// const EditProfile = () => {
//   const dispatch = useDispatch();
//   const selector = useSelector((s) => s.AuthSlice);

//   const loader = selector.loader;
//   const getAdminAllData = selector.getAdminAllData || [];

//   const [name, setName] = useState(getAdminAllData.name);
//   const [phone, setPhone] = useState(getAdminAllData.phone);
//   const [role, setRole] = useState(getAdminAllData.role);
//   const [edit, setEdit] = useState(false);
//   const [showChangePassword, setShowChangePassword] = useState(false);
//   const [showCurrent, setShowCurrent] = useState(false);
//   const [showNew, setShowNew] = useState(false);
//   const [showConfirm, setShowConfirm] = useState(false);

//   const {
//     control,
//     handleSubmit,
//     formState: { errors },
//     reset,
//   } = useForm({
//     resolver: yupResolver(changePasswordSchema),
//   });

//   const updateAdminProfile = () => {

//     const adminData = {
//       name: name,
//       phone: phone,
//     };

//     dispatch(updateAdmin(adminData))
//       .unwrap()
//       .then((data) => {
//         if (data?.success) {
//           Notify({ data: 'success', msg: data.message });
//           setEdit(false);
//           dispatch(getAdminData(true));
//         } else {
//           Notify({ data: 'error', msg: data?.message || 'Failed to update profile' });
//         }
//       })
//       .catch((error) => {
//         console.log('Update error:', error);
//         Notify({ data: 'error', msg: 'Something went wrong!' });
//       });
//   };

//   const handleEdit = () => {
//     setEdit(true);
//   };

//   const handleChangePassword = (data) => {
//     dispatch(changePassword({ currentPassword: data.currentPassword, newPassword: data.newPassword }))
//       .unwrap()
//       .then((data) => {
//         if (data.success) {
//           Notify({ data: 'success', msg: data.message });
//           setShowChangePassword(false);
//           reset(); // Reset form fields
//         } else {
//           Notify({ data: 'error', msg: data.message });
//         }
//         setTimeout(() => {
//           dispatch(popUp(false));
//         }, 5000);
//       })
//       .catch((error) => {
//         console.log('Change password error:', error);
//         Notify({ data: 'error', msg: 'Something went wrong!' });
//       });
//   };

//   const handleBack = () => {
//     setShowChangePassword(false);
//     reset(); // Reset form fields
//   };

//   const handlePhoneChange = (e) => {
//     const value = e.target.value.replace(/\D/g, '');
//     setPhone(value);
//   };

//   useEffect(() => {
//     if (getAdminAllData) {
//       setName(getAdminAllData.name);
//       setPhone(getAdminAllData.phone);
//     }
//   }, [getAdminAllData]);

//   useEffect(() => {
//     dispatch(getAdminData());
//   }, [dispatch]);

//   return (
//     <div className="edit-profile-main">
//       <div className="logo-container">
//         <img className="judicial-logo" src={judicialLogo} alt="Judicial Logo" />
//       </div>

//       <div className="edit-profile-container">
//         {showChangePassword ? (
//           <form className="profile-form" onSubmit={handleSubmit(handleChangePassword)}>
//             <h2>Change Password</h2>

//             {loader ? (
//               <div className="spinner-wrapper">
//                 <Spinner />
//               </div>
//             ) : (
//               <>
//                 <div className="input-group password-input">
//                   <Controller
//                     name="currentPassword"
//                     control={control}
//                     defaultValue=""
//                     render={({ field }) => (
//                       <TextField
//                         placeholder="Current Password"
//                         label=""
//                         type={showCurrent ? 'text' : 'password'}
//                         fullWidth
//                         variant="outlined"
//                         error={!!errors.currentPassword}
//                         helperText={errors.currentPassword?.message}
//                         {...field}
//                         InputLabelProps={{ shrink: false }}
//                         sx={{
//                           '& .MuiOutlinedInput-root': {
//                             borderRadius: '8px',
//                             background: '#ffffff',
//                             '& fieldset': { borderColor: '#e5e7eb' },
//                             '&:hover fieldset': { borderColor: '#d1d5db' },
//                             '&.Mui-focused fieldset': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)' },
//                           },
//                           '& .MuiInputBase-input': {
//                             color: '#1f2937',
//                           },
//                         }}
//                       />
//                     )}
//                   />
//                   <span className="toggle-password" onClick={() => setShowCurrent(!showCurrent)}>
//                     {showCurrent ? <FiEyeOff /> : <FiEye />}
//                   </span>
//                 </div>

//                 <div className="input-group password-input">
//                   <Controller
//                     name="newPassword"
//                     control={control}
//                     defaultValue=""
//                     render={({ field }) => (
//                       <TextField
//                         placeholder="New Password"
//                         label=""
//                         type={showNew ? 'text' : 'password'}
//                         fullWidth
//                         variant="outlined"
//                         error={!!errors.newPassword}
//                         helperText={errors.newPassword?.message}
//                         {...field}
//                         InputLabelProps={{ shrink: false }}
//                         sx={{
//                           '& .MuiOutlinedInput-root': {
//                             borderRadius: '8px',
//                             background: '#ffffff',
//                             '& fieldset': { borderColor: '#e5e7eb' },
//                             '&:hover fieldset': { borderColor: '#d1d5db' },
//                             '&.Mui-focused fieldset': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)' },
//                           },
//                           '& .MuiInputBase-input': {
//                             color: '#1f2937',
//                           },
//                         }}
//                       />
//                     )}
//                   />
//                   <span className="toggle-password" onClick={() => setShowNew(!showNew)}>
//                     {showNew ? <FiEyeOff /> : <FiEye />}
//                   </span>
//                 </div>

//                 <div className="input-group password-input">
//                   <Controller
//                     name="confirmNewPassword"
//                     control={control}
//                     defaultValue=""
//                     render={({ field }) => (
//                       <TextField
//                         placeholder="Confirm New Password"
//                         label=""
//                         type={showConfirm ? 'text' : 'password'}
//                         fullWidth
//                         variant="outlined"
//                         error={!!errors.confirmNewPassword}
//                         helperText={errors.confirmNewPassword?.message}
//                         {...field}
//                         InputLabelProps={{ shrink: false }}
//                         sx={{
//                           '& .MuiOutlinedInput-root': {
//                             borderRadius: '8px',
//                             background: '#ffffff',
//                             '& fieldset': { borderColor: '#e5e7eb' },
//                             '&:hover fieldset': { borderColor: '#d1d5db' },
//                             '&.Mui-focused fieldset': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)' },
//                           },
//                           '& .MuiInputBase-input': {
//                             color: '#1f2937',
//                           },
//                         }}
//                       />
//                     )}
//                   />
//                   <span className="toggle-password" onClick={() => setShowConfirm(!showConfirm)}>
//                     {showConfirm ? <FiEyeOff /> : <FiEye />}
//                   </span>
//                 </div>

//                 <div className="button-group">
//                   <Button
//                     type="button"
//                     variant="contained"
//                     className="reset-button"
//                     onClick={handleBack}
//                     sx={{
//                       background: '#6b7280',
//                       borderRadius: '12px',
//                       padding: '12px 24px',
//                       fontSize: '16px',
//                       fontWeight: 600,
//                       textTransform: 'none',
//                       '&:hover': {
//                         background: '#4b5563',
//                       },
//                     }}
//                   >
//                     Back
//                   </Button>
//                   <Button
//                     type="submit"
//                     variant="contained"
//                     className="edit-button"
//                     sx={{
//                       background: '#3b82f6',
//                       borderRadius: '12px',
//                       padding: '12px 24px',
//                       fontSize: '16px',
//                       fontWeight: 600,
//                       textTransform: 'none',
//                       '&:hover': {
//                         background: '#2563eb',
//                         boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
//                       },
//                     }}
//                   >
//                     Update Password
//                   </Button>
//                 </div>
//               </>
//             )}
//           </form>
//         ) : (
//           <form className="profile-form">
//             <h2>Edit Profile</h2>

//             {loader ? (
//               <div className="spinner-wrapper">
//                 <Spinner />
//               </div>
//             ) : (
//               <>
//                 <div className="input-group">
//                   <label className="fw-bold">Full Name</label>
//                   <input
//                     type="text"
//                     value={edit ? name : getAdminAllData.name}
//                     onChange={(e) => setName(e.target.value)}
//                     disabled={!edit}
//                   />
//                 </div>

//                 <div className="input-group">
//                   <label className="fw-bold">Phone Number</label>
//                   <input
//                     type="tel"
//                     value={edit ? phone : getAdminAllData.phone}
//                     onChange={handlePhoneChange}
//                     disabled={!edit}
//                     maxLength="10"
//                     pattern="[0-9]{10}"
//                     placeholder="Enter 10-digit phone number"
//                     required
//                   />
//                 </div>

//                 <div className="input-group">
//                   <label className="fw-bold">Role</label>
//                   <input
//                     type="text"
//                     value={getAdminAllData.role}
//                     onChange={(e) => setRole(e.target.value)}
//                     disabled
//                   />
//                 </div>

//                 <div className="input-group">
//                   <label className="fw-bold">Email</label>
//                   <input
//                     type="email"
//                     value={getAdminAllData.email}
//                     disabled
//                   />
//                 </div>

//                 <div className="button-group">
//                   <button
//                     type="button"
//                     className="edit-button"
//                     onClick={() => (!edit ? handleEdit() : updateAdminProfile())}
//                   >
//                     {edit ? 'Update Profile' : 'Edit Profile'}
//                   </button>
//                   <button
//                     type="button"
//                     className="reset-button"
//                     onClick={() => setShowChangePassword(true)}
//                   >
//                     Change Password
//                   </button>
//                 </div>
//               </>
//             )}
//           </form>
//         )}
//       </div>
//     </div>
//   );
// };

// export default EditProfile;